import {useEffect, useState, useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {fetchOneEntry, isPreviewing, subscribeToEditor} from '@builder.io/sdk-react'
import {responseSizeAcceptable} from 'hec/utils/responseUtils.tsx'
import {isValidBuilderOptions} from 'hec/utils/common'

// FetchOneEntry but also listen to PageModel changes
export const useFetchOneEntryWithListener = ({queryKey, options, enabled = true}) => {
    const apiKey = getConfig().app.builder.api

    const userAttributes = {}

    // Add validation for required options
    const isValidOptions = isValidBuilderOptions(options)
    const shouldActuallyFetch = enabled && isValidOptions

    if (isValidOptions) {
        options.userAttributes = {
            ...userAttributes,
            ...options.userAttributes
        }
    }

    // Log warning if options are invalid but fetch was enabled
    if (enabled && !isValidOptions) {
        console.warn('useFetchOneEntryWithListener: Invalid options provided', {
            options,
            enabled,
            modelType: typeof options?.model,
            queryKey
        })
    }

    const [previewData, setPreviewData] = useState(null)

    const {data: queryData, ...queryRest} = useQuery({
        queryKey: [
            ...(typeof queryKey === 'string'
                ? [queryKey]
                : Array.isArray(queryKey)
                ? queryKey
                : ['Builder-Fetch-Content'])
        ],
        queryFn: async () => {
            // Double-check validation before making API call
            if (!options?.model) {
                throw new Error('Model is required for Builder.io API call')
            }

            const response = await fetchOneEntry({
                apiKey,
                ...options
            })
            if (responseSizeAcceptable(response, [queryKey, options])) {
                return response
            } else {
                console.error(new Error('Response size exceeds acceptable limits'))
                return null
            }
        },
        enabled: shouldActuallyFetch
    })

    const memoData = useMemo(() => {
        return previewData ?? queryData
    }, [previewData, queryData])

    useEffect(() => {
        // subscribeToEditor() returns an unsubscribe cleanup function
        // Run this on the useEffect cleanup to avoid memory leaks
        // Only Subscribe when in Preview mode
        if (isPreviewing()) return subscribeToEditor(options.model, (data) => setPreviewData(data))
    }, [options.model])

    return {data: memoData, ...queryRest, apiKey}
}
